import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const SetNewPassword: React.FC = () => {
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [isButtonEnabled, setIsButtonEnabled] = useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordLengthError, setPasswordLengthError] = useState<string>("");
  const [confirmPasswordLengthError, setConfirmPasswordLengthError] = useState<string>("");

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword((prevState) => !prevState);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prevState) => !prevState);
  };

  const passwordConditions = [
    {
      text: "One lowercase character",
      isValid: /[a-z]/.test(newPassword),
    },
    {
      text: "One uppercase character",
      isValid: /[A-Z]/.test(newPassword),
    },
    {
      text: "One number",
      isValid: /[0-9]/.test(newPassword),
    },
    {
      text: "6 characters minimum",
      isValid: newPassword.length >= 6,
    },
  ];

  useEffect(() => {
    const allConditionsMet =
      passwordConditions.every((condition) => condition.isValid) &&
      newPassword === confirmPassword &&
      newPassword !== "";
    setIsButtonEnabled(allConditionsMet);
  }, [newPassword, confirmPassword, passwordConditions]);

  return (
    <div className="col-span-12 h-full flex items-center lg:col-span-3 lg:col-start-9 relative">
      <div className="w-full lg:pb-0 pb-14">
        <img className="w-40" src="/images/logo.svg" alt="Logo" />
        <p className="text-2xl mt-8 font-semibold text-grease-100">
          Set new password
        </p>
        <div className="mt-6">
          <label className="text-xs text-lightgray-200">New Password</label>
          <div className="relative mt-2">
            <input
              id="new-password"
              type={showNewPassword ? "text" : "password"}
              className="block w-full p-4 mt-2 rounded-lg bg-lightblue-100 text-sm text-grease-100 placeholder-gray-400 focus:outline-none"
              placeholder="Enter password"
              value={newPassword}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length > 20) {
                  setPasswordLengthError("Password must be 20 characters or less");
                  setNewPassword(value.substring(0, 20));
                } else {
                  setPasswordLengthError("");
                  setNewPassword(value);
                }
              }}
              onKeyDown={(e) => {
                if (newPassword.length >= 20 && 
                    e.key !== 'Backspace' && 
                    e.key !== 'Delete' && 
                    e.key !== 'ArrowLeft' && 
                    e.key !== 'ArrowRight' &&
                    e.key !== 'Tab' &&
                    e.key !== 'Home' &&
                    e.key !== 'End' &&
                    !e.ctrlKey && 
                    !e.metaKey &&
                    !e.altKey) {
                  e.preventDefault();
                  setPasswordLengthError("Password must be 20 characters or less");
                }
              }}
              minLength={6}
              maxLength={20}
            />
            {passwordLengthError && <p className="text-red-500 mt-1 text-xs">{passwordLengthError}</p>}
            <button
              type="button"
              onClick={toggleNewPasswordVisibility}
              className="absolute inset-y-0 end-0 flex items-center z-20 px-3 cursor-pointer text-gray-400 rounded-e-md focus:outline-none focus:text-blue-600"
            >
              <svg
                className="shrink-0 size-3.5"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                {showNewPassword ? (
                  <>
                    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </>
                ) : (
                  <>
                    <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"></path>
                    <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"></path>
                    <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"></path>
                    <line x1="2" x2="22" y1="2" y2="22"></line>
                  </>
                )}
              </svg>
            </button>
          </div>
        </div>
        <div className="mt-2">
          <label className="text-xs text-lightgray-200">Confirm Password</label>
          <div className="relative mt-2">
            <input
              id="confirm-password"
              type={showConfirmPassword ? "text" : "password"}
              className="block w-full p-4 mt-2 border border-royalblue-100 rounded-lg bg-lightblue-100 text-sm text-grease-100 placeholder-gray-400 focus:outline-none"
              placeholder="Confirm password"
              value={confirmPassword}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length > 20) {
                  setConfirmPasswordLengthError("Password must be 20 characters or less");
                  setConfirmPassword(value.substring(0, 20));
                } else {
                  setConfirmPasswordLengthError("");
                  setConfirmPassword(value);
                }
              }}
              onKeyDown={(e) => {
                if (confirmPassword.length >= 20 && 
                    e.key !== 'Backspace' && 
                    e.key !== 'Delete' && 
                    e.key !== 'ArrowLeft' && 
                    e.key !== 'ArrowRight' &&
                    e.key !== 'Tab' &&
                    e.key !== 'Home' &&
                    e.key !== 'End' &&
                    !e.ctrlKey && 
                    !e.metaKey &&
                    !e.altKey) {
                  e.preventDefault();
                  setConfirmPasswordLengthError("Password must be 20 characters or less");
                }
              }}
              minLength={6}
              maxLength={20}
            />
            {confirmPasswordLengthError && <p className="text-red-500 mt-1 text-xs">{confirmPasswordLengthError}</p>}
            <button
              type="button"
              onClick={toggleConfirmPasswordVisibility}
              className="absolute inset-y-0 end-0 flex items-center z-20 px-3 cursor-pointer text-gray-400 rounded-e-md focus:outline-none focus:text-blue-600"
            >
              <svg
                className="shrink-0 size-3.5"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                {showConfirmPassword ? (
                  <>
                    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </>
                ) : (
                  <>
                    <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"></path>
                    <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"></path>
                    <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"></path>
                    <line x1="2" x2="22" y1="2" y2="22"></line>
                  </>
                )}
              </svg>
            </button>
          </div>
        </div>
        <div className="mt-8">
          {passwordConditions.map((condition, index) => (
            <div className="flex gap-2 mt-3 items-center" key={index}>
              <div
                className={`flex items-center justify-center w-4 h-4 text-xs font-medium rounded-full ${condition.isValid ? "bg-royalblue-100" : "bg-indigo-100"}`}
              >
                {condition.isValid && <img src="/images/check.svg" alt="Valid" />}
              </div>
              <p className="text-lightgray-200 font-normal text-xs">
                {condition.text}
              </p>
            </div>
          ))}
        </div>
        <button
          className={`w-full p-4 rounded-lg text-white text-center text-sm mt-6 transition-opacity ${isButtonEnabled ? "bg-royalblue-100 opacity-100" : "bg-royalblue-100 opacity-55"}`}
          disabled={!isButtonEnabled}
        >
          Reset Password
        </button>
        <div
          className="flex items-center gap-2 mt-8 cursor-pointer"
          onClick={() => navigate("/login")}
        >
          <i className="st-arrow-left text-lightgray-200 text-xs"></i>
          <p className="text-xs text-lightgray-200 font-medium">Back to log in</p>
        </div>
      </div>
      <div className="flex items-center gap-2 absolute bottom-0 left-0">
        <div className="w-6 h-1 rounded-xl bg-indigo-200"></div>
        <div className="w-6 h-1 rounded-xl bg-indigo-200"></div>
        <div className="w-6 h-1 rounded-xl bg-royalblue-100"></div>
      </div>
    </div>
  );
};

export default SetNewPassword;
